import React from "react";

import { PrefillVariation } from "../types";
import { toPrefillableCosts, toPrefillVariations } from "../utils";

import { BUSINESS_UNIT_TO_CODE_MAP } from "@/constants/region";
import { useUser } from "@/contexts/UserContext";
import { useGetWorkers } from "@/hooks/chime";
import { useGetAllInternalPlantItems } from "@/hooks/plantItems";
import { useGetSubcontractors } from "@/hooks/useGetSubcontractors";
import { IProjectCostInput } from "@/pages/ForecastingPage";
import { IForecastingRes } from "@/types/forecasting";

/**
 * Copies a saved forecast's costs into the wizard — the month being
 * reforecast, or the previous month for a new forecast.
 *
 * Runs on the page rather than inside the cost step: the step unmounts on
 * every step change, so pre-filling there re-applied the source on each
 * return and wiped the user's edits. Each source is applied once.
 */
export const useForecastCostPrefill = ({
  source,
  includeVariations,
  setProjectCosts,
  setVariations
}: {
  source?: IForecastingRes;
  includeVariations: boolean;
  setProjectCosts: React.Dispatch<React.SetStateAction<IProjectCostInput>>;
  setVariations: React.Dispatch<React.SetStateAction<PrefillVariation[]>>;
}) => {
  const user = useUser();
  const businessUnitId = user?.regionCode
    ? BUSINESS_UNIT_TO_CODE_MAP[user.regionCode]
    : undefined;

  // Same queries the cost step uses, so these are cache hits there.
  const { data: workers = [], isLoading: workersLoading } =
    useGetWorkers(businessUnitId);
  const { data: plantItems, isLoading: plantItemsLoading } =
    useGetAllInternalPlantItems();
  const { data: subcontractors = [], isLoading: subcontractorsLoading } =
    useGetSubcontractors();

  const appliedSourceRef = React.useRef<IForecastingRes>();

  React.useEffect(() => {
    if (!source?.budgetForecasts || appliedSourceRef.current === source) return;
    // Wait for the lookups so copied entries are priced at today's rates
    // and subcontractors get their trade back.
    if (workersLoading || plantItemsLoading || subcontractorsLoading) return;

    appliedSourceRef.current = source;
    setProjectCosts((prev) => ({
      ...prev,
      ...toPrefillableCosts(
        source.budgetForecasts,
        workers,
        plantItems?.value ?? [],
        subcontractors
      )
    }));
    if (includeVariations && source.variations?.length > 0) {
      setVariations(toPrefillVariations(source.variations));
    }
  }, [
    source,
    includeVariations,
    workers,
    workersLoading,
    plantItems,
    plantItemsLoading,
    subcontractors,
    subcontractorsLoading,
    setProjectCosts,
    setVariations
  ]);
};
